import { useId, useRef, useState } from 'react';
import { Alert, Box, Button, LinearProgress, Stack, Typography } from '@mui/material';
import CheckCircleOutlineRounded from '@mui/icons-material/CheckCircleOutlineRounded';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import UploadFileOutlined from '@mui/icons-material/UploadFileOutlined';
import { AttachmentInfo } from '../../Models/ReWeightingModels/Documents';
import DocumentActions from './DocumentActions';
import dayjs from 'dayjs';

interface Props {
  attachment: AttachmentInfo | null;
  load: () => Promise<Blob>;
  onUpload?: (file: File) => Promise<void>;
  disabled?: boolean;
  uploading?: boolean;
}

export default function AttachmentField({ attachment, load, onUpload, disabled, uploading }: Props) {
  const titleId = useId();
  const input = useRef<HTMLInputElement>(null);
  const [error, setError] = useState('');
  const upload = async (file?: File) => {
    if (!file || !onUpload) return;
    setError('');
    if (!/\.(pdf|xlsx)$/i.test(file.name) || file.size <= 0 || file.size > 10 * 1024 * 1024) {
      setError('Veldu PDF eða Excel-skjal (.xlsx), að hámarki 10 MB.'); return;
    }
    // Villur frá þjónustunni birtast hjá nótunni; innsláttur er ekki falinn við mistök.
    try { await onUpload(file); } catch { /* Nótan birtir villuna og leið til endurhleðslu. */ }
  };
  return <Box component="section" aria-labelledby={titleId} aria-busy={!!uploading}>
    <Stack direction="row" alignItems="baseline" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ mb: 1.25 }}>
      <Typography id={titleId} component="h3" variant="subtitle2" fontWeight={700}>
        {attachment?.submittedAt ? onUpload ? 'Fylgiskjal úr fyrri skilum' : 'Innsent fylgiskjal' : 'Fylgiskjal með skilum'}
      </Typography>
      {onUpload && attachment && <Button size="small" disabled={disabled || uploading} onClick={() => input.current?.click()}>
        {attachment.submittedAt ? 'Velja nýtt fylgiskjal' : 'Skipta um skjal'}</Button>}
    </Stack>
    <Box sx={{ position: 'relative', overflow: 'hidden', border: 1, borderStyle: attachment ? 'solid' : 'dashed', borderColor: 'divider', borderRadius: 1.5,
      bgcolor: 'background.default', p: { xs: 1.5, sm: 2 } }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={2}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
          <Box sx={{ color: 'primary.main', flexShrink: 0, display: 'flex', alignItems: 'center' }}>
            {attachment ? <DescriptionOutlined sx={{ fontSize: 32 }} /> : <UploadFileOutlined sx={{ fontSize: 32 }} />}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} sx={{ overflowWrap: 'anywhere' }}>
              {attachment?.fileName ?? (onUpload ? 'Bættu við gögnum úr endurvigtuninni' : 'Ekkert fylgiskjal hefur verið skráð.')}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              {attachment ? `${attachment.contentType === 'application/pdf' ? 'PDF' : 'Excel'} · ${(attachment.size / 1024).toLocaleString('is-IS', { maximumFractionDigits: 0 })} KB`
                : onUpload ? 'PDF eða Excel (.xlsx), að hámarki 10 MB.' : null}
            </Typography>
            {attachment && <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.75, color: attachment.submittedAt ? 'success.main' : 'text.secondary' }}>
              {attachment.submittedAt && <CheckCircleOutlineRounded sx={{ fontSize: 15, flexShrink: 0 }} />}
              <Typography variant="caption">{attachment.submittedAt
                ? `Skilað ${dayjs(attachment.submittedAt).format('DD.MM.YYYY [kl.] HH:mm')}`
                : `Vistað ${dayjs(attachment.uploadedAt).format('DD.MM.YYYY [kl.] HH:mm')} · Bíður skila`}</Typography>
            </Stack>}
          </Box>
        </Stack>
        {attachment ? <DocumentActions key={`${attachment.noteId}:${attachment.uploadedAt}:${attachment.fileName}`} fileName={attachment.fileName} load={load}
          canPreview={attachment.contentType === 'application/pdf'} disabled={disabled || uploading} />
          : onUpload && <Button size="small" variant="outlined" startIcon={<UploadFileOutlined />} sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
            disabled={disabled || uploading} onClick={() => input.current?.click()}>{uploading ? 'Hleð upp…' : 'Velja skjal'}</Button>}
      </Stack>
      {uploading && <LinearProgress aria-label="Hleð upp fylgiskjali" sx={{ position: 'absolute', left: 0, right: 0, bottom: 0 }} />}
    </Box>
    {onUpload && <>
      <input ref={input} type="file" accept=".pdf,.xlsx" aria-label="Velja fylgiskjal endurvigtunar" hidden
        onChange={event => { const file = event.target.files?.[0]; event.target.value = ''; void upload(file); }} />
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>Skjalið fylgir skilunum til hafnar og læsist þegar þau eru staðfest.</Typography>
    </>}
    {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
  </Box>;
}
